import * as React from 'react';
import { cn } from '@noor/lib/cn';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../dialog';
import { useModalControls } from './modal-context';

/**
 * Dialog shell for a registered modal. Reads open/close from context so feature
 * modals don't manage that state themselves. The payload arrives as a typed
 * prop from the host (`ModalRenderProps`), not from context:
 *
 * ```tsx
 * function CreateNoteModal({ payload, close }: ModalRenderProps<'create-note'>) {
 *   return (
 *     <Modal title="Add note" description={payload.orderId}>
 *       …form…
 *     </Modal>
 *   );
 * }
 * ```
 *
 * Closing (Esc or the X/cancel buttons — outside clicks never dismiss by
 * default) calls the context `close()`, which pops the instance off the store
 * after Radix's exit animation.
 */
export interface ModalProps {
  title: React.ReactNode;
  description?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
  /** Block Esc dismissal (e.g. while a mutation is pending). Outside clicks never dismiss by default. */
  dismissible?: boolean;
  /**
   * Whether a click/tap outside the modal dismisses it. Defaults to FALSE:
   * modals render an explicit Cancel/close affordance, so an accidental
   * backdrop click must not discard the user's input. Escape is unaffected
   * and stays gated by `dismissible`.
   */
  closeOnInteractOutside?: boolean;
  /**
   * Whether opening the modal moves focus to its first focusable child.
   *
   * Defaults to FALSE, unlike Radix. Radix lands on the first input and selects
   * its text, which on a form opened over existing data reads as an invitation
   * to rewrite a field the user didn't come to touch — and is one keystroke
   * from doing it. On mobile it also springs the keyboard open over the form.
   *
   * Focus still moves to the dialog container, so the modal stays
   * keyboard-reachable and the focus trap holds; Tab from there reaches the
   * first field as usual. Pass `true` for a modal whose whole purpose is one
   * input the user is expected to type into immediately.
   *
   * Deliberately NOT named `autoFocus` — that is a DOM attribute name and trips
   * `jsx-a11y/no-autofocus` at every call site, and this controls Radix's
   * `onOpenAutoFocus`, not the attribute.
   */
  focusOnOpen?: boolean;
}

export function Modal({
  title,
  description,
  children,
  footer,
  className,
  dismissible = true,
  closeOnInteractOutside = false,
  focusOnOpen = false,
}: ModalProps) {
  const { open, close } = useModalControls();

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && dismissible) close();
      }}
    >
      <DialogContent
        className={cn(className)}
        closeOnInteractOutside={closeOnInteractOutside}
        {...(focusOnOpen
          ? {}
          : {
              onOpenAutoFocus: (e: Event) => {
                e.preventDefault();
                // Radix focuses the first focusable child; without a target of
                // our own the focus would fall to <body> and leave the trap.
                (e.currentTarget as HTMLElement | null)?.focus();
              },
            })}
        {...(dismissible
          ? {}
          : {
              onEscapeKeyDown: (e) => e.preventDefault(),
            })}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description ? <DialogDescription>{description}</DialogDescription> : null}
        </DialogHeader>
        {children}
        {footer ? <DialogFooter>{footer}</DialogFooter> : null}
      </DialogContent>
    </Dialog>
  );
}
